/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import {
    Autocomplete,
    Box,
    CircularProgress,
    InputAdornment,
    TextField,
    Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useEffect, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import FormBox from '../../common/FormBox/FormBox';
import { EARLIEST_BEGIN_DATE } from '../../../data/constants/constants';
import { formCounterSlice } from '../../../redux/formCounter';
import { CitizenshipInfoData, Spouse } from './GeneralInfo';
import ToggleButtonGroup from 'components/common/ToggleButtonGroup/ToggleButtonGroup';
import PartnerInfoSwitch from './PartnerInfoSwitch';
import CountryAutocomplete from '../CountryForm';
import { useAuth } from 'contexts/ProtectedRoute';
import { RootState } from 'redux/store';
import { isSalesPersonRole } from 'data/constants/roles';
import JobTitleSelector from './JobTitle';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    enabledMarketFields,
    marketFields,
    percentTimeTooltipKey,
    rowCountryEnabled,
    type OccupationIdKey,
} from 'utils/countries';
import type { CountryCode } from 'types/countries';
import api from 'api';
import { fetchOptionsReturn } from './utils';
import BaseInput from 'components/common/BaseInput';
import { startOfDay } from 'date-fns';
import Button from 'views/Dashboards-B2B/components/Button';
import { ReactComponent as AIIcon } from '../../../assets/icons/AI.svg';
interface Entry {
    start: Date | null;
    end: Date | null;
    current: boolean | undefined;
    country: string | null;
    region: string | null;
    percent_time: string | null;

    work_permit: string | null;
    zip: string | null;
    self_employed: boolean | undefined;
    occupation_au_id: number | null;
    occupation_ca_id: number | null;
    occupation_uk_id: number | null;
}

const initEntry: Entry = {
    start: null,
    end: null,
    current: undefined,
    country: '',
    region: '',
    percent_time: '100',

    work_permit: '',
    zip: '',
    self_employed: false,
    occupation_au_id: null,
    occupation_ca_id: null,
    occupation_uk_id: null,
};

interface Msg {
    start: string;
    end: string;
    current: string;
    country: string;
    region: string;
    percent_time: string;
    self_employed: string;
    // profession_aus: string;
    // profession_can: string;
    work_permit: string;
    zip: string;
    occupation_au_id: string;
    occupation_ca_id: string;
    occupation_uk_id: string;
}

const initMsg: Msg = {
    start: '',
    end: '',
    current: '',
    country: '',
    region: '',
    percent_time: '',
    // profession_aus: '',
    // profession_can: '',
    work_permit: '',
    zip: '',
    self_employed: '',
    occupation_au_id: '',
    occupation_ca_id: '',
    occupation_uk_id: '',
};
export interface Profession {
    country_code: string;
    id: number;
    name: string;
    code_type?: string;
}

export default function WorkExperienceV2() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes } = useEnabledCountries();
    const tableName = 'WorkExperienceNew';
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([initMsg]);
    const { setShowSpouse } = formCounterSlice.actions;
    const [hasExperience, setHasExperience] = useState<boolean>(false);
    const dispatch = useDispatch();

    const [, setHasSpouse] = useState<boolean>(false);
    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [professionsByCode, setProfessionsByCode] = useState<
        Partial<Record<CountryCode, Profession[]>>
    >({});
    const [occupationAssist, setOccupationAssist] = useState<{
        index: number;
        code: CountryCode;
    } | null>(null);

    const [options, setOptions] = useState({
        workPermitOptions: [''],
        countryOptions: [''],
        regionOptions: {} as { [key: string]: string[] },
    });

    useEffect(() => {
        loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        const markets = enabledMarketFields(codes);
        if (markets.length === 0) {
            setProfessionsByCode({});
            return;
        }
        Promise.all(
            markets.map(async (market) => {
                try {
                    const resp = await api?.assessment_options.getIndexProfessions(market.code);
                    const list =
                        resp?.status === 200 || resp?.status === 201
                            ? ((resp.data as Profession[]) ?? [])
                            : [];
                    return [market.code, list] as const;
                } catch (err) {
                    console.log(err);
                    return [market.code, [] as Profession[]] as const;
                }
            })
        ).then((entries) => {
            if (!cancelled) {
                setProfessionsByCode(Object.fromEntries(entries));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [codes]);

    const loadOptions = async () => {
        const [countries, regions, workpermit] = await Promise.all([
            fetchOptionsReturn(api?.assessment_options.getCountryOptions),
            fetchOptionsReturn(api?.assessment_options.getRegionOptions),
            fetchOptionsReturn(api?.assessment_options.getWorkPermitOptions),
        ]);

        setOptions({
            countryOptions: countries || [''],
            workPermitOptions: workpermit || [''],
            regionOptions: regions || {},
        });
    };

    const percentTimeOptions = Array(10)
        .fill(0)
        .map((_, i) => String((i + 1) * 10))
        .reverse();
    useEffect(() => {
        if (isSpouseInfo && isUserData) {
            setIsLoading(false);
        }
    }, [isSpouseInfo, isUserData]);

    const loadData = (data: Entry[]) => {
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(data);
            if (data.length > 0) {
                setHasExperience(true);
            }
        }
        if (data == null || data) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    const handleSpouse = async (data: Spouse) => {
        if (data && Object.keys(data).length) {
            setHasSpouse(true);
        }
        if (data == null || data) {
            setIsSpouseInfo(true);
        } else {
            setIsSpouseInfo(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'WorkExperienceForm',
        });
    }, []);

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    const isSalesPerson = isSalesPersonRole(role);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setIsSpouseInfo(false);
            setData([]);
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(loadData, tableName, candidate_id);
                api.tableInput.fetchCandidateInputSales(handleSpouse, 'Spouse', candidate_id);
                api.tableInput.fetchCandidateInputSales(
                    loadCitizenshipInfo,
                    'Citizenship',
                    candidate_id
                );
            } else {
                api.tableInput.fetchCandidateInput(loadData, tableName);
                api.tableInput.fetchCandidateInput(handleSpouse, 'Spouse');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, isAuthenticated]);

    const [citizenshipInfo, setCitizenshipInfo] = useState<string>('');

    const loadCitizenshipInfo = async (data: CitizenshipInfoData[]) => {
        if (data && Object.keys(data).length) {
            const countriesString = data
                .filter((item) => item.country !== null)
                .map((item) => item.country)
                .join(', ');
            setCitizenshipInfo(countriesString);
        }
    };

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];

        for (let i = 0; i < data.length; i++) {
            for (const key of Object.keys(initEntry)) {
                const value = data[i][key as keyof Entry];
                let msg = '';

                if (key === 'region' || key === 'work_permit') {
                    if (
                        !['Australia', 'Canada', 'United Kingdom'].includes(
                            data[i].country as string
                        )
                    )
                        continue;
                }
                if (key === 'end' && data[i].current) continue;
                const occupationFields = [
                    'occupation_uk_id',
                    'occupation_au_id',
                    'occupation_ca_id',
                ];
                const requiredOccupationFields = new Set(
                    enabledMarketFields(codes).map((market) => market.occupationIdKey)
                );
                if (
                    key === 'profession_can' ||
                    key === 'profession_aus' ||
                    key === 'zip' ||
                    key === 'work_permit' ||
                    (occupationFields.includes(key) &&
                        !requiredOccupationFields.has(key as OccupationIdKey))
                ) {
                    continue;
                }
                if (value === '' || value === undefined || value === null) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const handleUpdate = (idx: number, field: string, value: string | boolean | null | number) => {
        const temp = [...data];
        if (field === 'country') {
            temp[idx] = { ...temp[idx], region: '' };
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], region: '', work_permit: '' };
            setValidationMsg(tempMsg);
        }
        if (field === 'current' && value === true) {
            // Clear the end date when the employment is marked current/ongoing.
            temp[idx] = { ...temp[idx], end: null };
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], end: '' };
            setValidationMsg(tempMsg);
        }
        if (
            (field === 'occupation_au_id' ||
                field === 'occupation_ca_id' ||
                field === 'occupation_uk_id') &&
            value
        ) {
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], [field]: '' };
            setValidationMsg(tempMsg);
        }
        temp[idx] = { ...temp[idx], [field]: value };
        setData(temp);
    };

    const handleDateUpdate = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: '' };
            setData(tmp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep month visible
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
            setData(tmp);
            return;
        }

        const temp = [...validationMsg];
        temp[idx] = { ...temp[idx], [field]: '' };
        setValidationMsg(temp);
        const tmp = [...data];
        tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
        if (field === 'start') {
            tmp[idx] = { ...tmp[idx], end: null };
        }
        setData(tmp);
    };

    const handleUpdateHasExperience = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasExperience(true);
            setData([initEntry]);
            setValidationMsg([initMsg]);
        } else if (value === t('no', { lng: language })) {
            setHasExperience(false);
            setData([]);
            setValidationMsg([]);
        }
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = initEntry;

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    function handleSwitchPartnerMe() {
        if (validate()) {
            if (isSalesPerson) {
                api.tableInput.postSales(data, tableName, candidate_id);
            } else {
                api.tableInput.post(data, tableName);
            }
            dispatch(setShowSpouse());
        }
    }

    const closeOccupationAssist = () => setOccupationAssist(null);

    const checkEndDateBeforeStart = (
        endDate: Date | null,
        startDate: Date | null | undefined,
        idx: number
    ) => {
        const normalizedStartDate = startDate ? startOfDay(new Date(startDate)) : undefined;
        const normalizedEndDate = endDate ? startOfDay(endDate) : undefined;

        if (normalizedEndDate && normalizedStartDate && normalizedEndDate < normalizedStartDate) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                end: t('endDateBeforeStartDate', { lng: language }),
            };
            setValidationMsg(temp);
            return true;
        }

        return false;
    };

    const markets = enabledMarketFields(codes);

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={[tableName]}>
                    <S.TitleContainer>
                        <div>
                            <S.Title style={{ marginBottom: '0.5rem' }}>
                                {t('workExperience', { lng: language })}
                            </S.Title>
                            <S.FormNote>{t('workExperienceNote', { lng: language })}</S.FormNote>
                        </div>
                        <PartnerInfoSwitch partnerActive={false} onSwitch={handleSwitchPartnerMe} />
                    </S.TitleContainer>
                    <div style={{ marginTop: '2rem' }}>
                        <ToggleButtonGroup
                            customStyle={true}
                            colForMobile={true}
                            options={[t('yes', { lng: language }), t('no', { lng: language })]}
                            name="has_experience"
                            label={t('recentWorkExperience', { lng: language })}
                            value={
                                hasExperience
                                    ? t('yes', { lng: language })
                                    : t('no', { lng: language })
                            }
                            onChange={(_: string, value: string) =>
                                handleUpdateHasExperience(value)
                            }
                        />
                    </div>
                    {hasExperience &&
                        data.map((_, i) => {
                            return (
                                <S.EntryCard key={i}>
                                    <S.SubTitle>
                                        <>{`${t('workExperience', { lng: language })} ${i + 1}`}</>
                                        <S.DeleteButton hidden={i === 0}>
                                            <button onClick={() => handleDeleteEntry(i)}>
                                                <FaTrash />
                                            </button>
                                        </S.DeleteButton>
                                    </S.SubTitle>

                                    <S.FlexColumn>
                                        <S.FlexColumn>
                                            <S.GridSection>
                                                <ToggleButtonGroup
                                                    options={[
                                                        t('yes', { lng: language }),
                                                        t('no', { lng: language }),
                                                    ]}
                                                    name="self_employed"
                                                    label={`*${t('areYouSelfEmployed', {
                                                        lng: language,
                                                    })}`}
                                                    value={
                                                        data[i].self_employed === undefined ||
                                                        data[i].self_employed === null
                                                            ? undefined
                                                            : data[i].self_employed
                                                              ? t('yes', { lng: language })
                                                              : t('no', { lng: language })
                                                    }
                                                    onChange={(arg1: string, arg2: string) =>
                                                        handleUpdate(
                                                            i,
                                                            arg1,
                                                            arg2 === t('yes', { lng: language })
                                                        )
                                                    }
                                                    error={validationMsg[i].self_employed}
                                                />
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        views={['year', 'month']}
                                                        minDate={new Date(EARLIEST_BEGIN_DATE)}
                                                        maxDate={new Date()}
                                                        openTo="year"
                                                        label={`*${t('startDate', {
                                                            lng: language,
                                                        })}`}
                                                        format="MM/yyyy"
                                                        value={parseStoredDate(data[i].start)}
                                                        onChange={(date: Date | null) => {
                                                            handleDateUpdate(i, 'start', date);
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error:
                                                                    validationMsg[i].start !== '',
                                                                helperText: validationMsg[i].start,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <ToggleButtonGroup
                                                    options={[
                                                        t('yes', { lng: language }),
                                                        t('no', { lng: language }),
                                                    ]}
                                                    name="current"
                                                    label={`*${t('currentEmployment', {
                                                        lng: language,
                                                    })}`}
                                                    value={
                                                        data[i].current === undefined ||
                                                        data[i].current === null
                                                            ? undefined
                                                            : data[i].current
                                                              ? t('yes', { lng: language })
                                                              : t('no', { lng: language })
                                                    }
                                                    onChange={(arg1: string, arg2: string) =>
                                                        handleUpdate(
                                                            i,
                                                            arg1,
                                                            arg2 === t('yes', { lng: language })
                                                        )
                                                    }
                                                    error={validationMsg[i].current}
                                                />

                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        views={['year', 'month']}
                                                        minDate={
                                                            parseStoredDate(data[i].start) ??
                                                            new Date(EARLIEST_BEGIN_DATE)
                                                        }
                                                        maxDate={new Date()}
                                                        disabled={data[i].current}
                                                        openTo="year"
                                                        label={`*${t('endDate', {
                                                            lng: language,
                                                        })}`}
                                                        format="MM/yyyy"
                                                        value={parseStoredDate(data[i].end)}
                                                        onChange={(date: Date | null) => {
                                                            // Commit typed value first so the field stays in sync
                                                            // (early return left partial years e.g. 0201 on screen).
                                                            handleDateUpdate(i, 'end', date);
                                                            if (date && isFullDateFormat(date)) {
                                                                checkEndDateBeforeStart(
                                                                    date,
                                                                    parseStoredDate(data[i].start),
                                                                    i
                                                                );
                                                            }
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error: validationMsg[i].end !== '',
                                                                helperText: validationMsg[i].end,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <CountryAutocomplete
                                                    data={data}
                                                    i={i}
                                                    countries={options.countryOptions}
                                                    handleUpdate={handleUpdate}
                                                    validationMsg={validationMsg}
                                                ></CountryAutocomplete>
                                                {[
                                                    'Australia',
                                                    'Australie',
                                                    'Canada',
                                                    'United Kingdom',
                                                    'Royaume Uni',
                                                ].includes(data[i].country as string) &&
                                                    !citizenshipInfo.includes(
                                                        data[i].country as string
                                                    ) && (
                                                        <S.FlexColumn>
                                                            <Autocomplete
                                                                id="employer_permit"
                                                                value={data[i].work_permit}
                                                                options={
                                                                    options.workPermitOptions || []
                                                                }
                                                                onChange={(_, v: string | null) =>
                                                                    handleUpdate(
                                                                        i,
                                                                        'work_permit',
                                                                        v
                                                                    )
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={t('employerPermit', {
                                                                            lng: language,
                                                                        })}
                                                                        error={Boolean(
                                                                            validationMsg[i]
                                                                                .work_permit
                                                                        )}
                                                                    />
                                                                )}
                                                            />

                                                            <S.ValidationMsg>
                                                                {validationMsg[i].work_permit}
                                                            </S.ValidationMsg>
                                                        </S.FlexColumn>
                                                    )}
                                                {[
                                                    'Australia',
                                                    'Australie',
                                                    'Canada',
                                                    'United Kingdom',
                                                    'Royaume Uni',
                                                ].includes(data[i].country as string) && (
                                                    <S.FlexColumn>
                                                        <Autocomplete
                                                            disablePortal
                                                            id="region"
                                                            value={data[i].region}
                                                            options={
                                                                options.regionOptions[
                                                                    data[i].country as string
                                                                ] || []
                                                            }
                                                            onChange={(_, value: string | null) =>
                                                                handleUpdate(i, 'region', value)
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={`*${t('region', {
                                                                        lng: language,
                                                                    })}`}
                                                                    error={Boolean(
                                                                        validationMsg[i].region
                                                                    )}
                                                                />
                                                            )}
                                                        />

                                                        <S.ValidationMsg>
                                                            {validationMsg[i].region}
                                                        </S.ValidationMsg>
                                                    </S.FlexColumn>
                                                )}
                                                {rowCountryEnabled(
                                                    codes,
                                                    data[i].country,
                                                    'AU'
                                                ) && (
                                                    <BaseInput
                                                        id="Zip"
                                                        label={`${t('zipLabel', { lng: language })}`}
                                                        type="text"
                                                        value={data[i].zip}
                                                        onChange={(e) => {
                                                            handleUpdate(i, 'zip', e.target.value);
                                                        }}
                                                        error={Boolean(validationMsg[i].zip)}
                                                        helperText={validationMsg[i].zip}
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <Tooltip
                                                                            title={t(
                                                                                'zipTooltipExperience',
                                                                                {
                                                                                    lng: language,
                                                                                }
                                                                            )}
                                                                            arrow
                                                                            enterTouchDelay={0}
                                                                            leaveTouchDelay={6000}
                                                                        >
                                                                            <InfoOutlinedIcon
                                                                                sx={{
                                                                                    color: '#686868',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: 20,
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                        }}
                                                    />
                                                )}
                                                <S.FlexColumn>
                                                    <Autocomplete
                                                        disablePortal
                                                        id="percent_time"
                                                        value={String(data[i].percent_time)}
                                                        options={percentTimeOptions}
                                                        onChange={(_, value: string | null) =>
                                                            handleUpdate(i, 'percent_time', value)
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t('percentOfFulltimeWork', {
                                                                    lng: language,
                                                                })}
                                                                error={Boolean(
                                                                    validationMsg[i].percent_time
                                                                )}
                                                                slotProps={{
                                                                    ...params.slotProps,
                                                                    input: {
                                                                        ...params.slotProps.input,
                                                                        endAdornment: (
                                                                            <>
                                                                                <Tooltip
                                                                                    title={t(
                                                                                        percentTimeTooltipKey(
                                                                                            codes
                                                                                        ),
                                                                                        {
                                                                                            lng: language,
                                                                                        }
                                                                                    )}
                                                                                    arrow
                                                                                    enterTouchDelay={
                                                                                        0
                                                                                    }
                                                                                    leaveTouchDelay={
                                                                                        6000
                                                                                    }
                                                                                >
                                                                                    <InfoOutlinedIcon
                                                                                        sx={{
                                                                                            color: '#686868',
                                                                                            cursor: 'pointer',
                                                                                            fontSize: 20,
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                                {
                                                                                    params.slotProps
                                                                                        .input
                                                                                        .endAdornment
                                                                                }
                                                                            </>
                                                                        ),
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <S.ValidationMsg>
                                                        {validationMsg[i].percent_time}
                                                    </S.ValidationMsg>
                                                </S.FlexColumn>
                                            </S.GridSection>
                                            <S.Text style={{ marginBottom: '0.75rem' }}>
                                                {t('accurateVisaResults', { lng: language })}
                                            </S.Text>

                                            <S.GridSection>
                                                {markets.map((market) => {
                                                    const options =
                                                        professionsByCode[market.code] ?? [];
                                                    const selected =
                                                        options.find(
                                                            (option) =>
                                                                option.id ===
                                                                data[i][market.occupationIdKey]
                                                        ) ?? null;
                                                    const occupationError =
                                                        validationMsg[i]?.[
                                                            market.occupationIdKey
                                                        ] ?? '';
                                                    return (
                                                        <Box
                                                            key={market.code}
                                                            sx={{ gridColumn: '1 / -1' }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: {
                                                                        xs: '1fr',
                                                                        sm: 'minmax(0, 1fr) auto',
                                                                    },
                                                                    columnGap: '15px',
                                                                    rowGap: '8px',
                                                                    alignItems: 'start',
                                                                }}
                                                            >
                                                                <Autocomplete
                                                                    disablePortal
                                                                    id={market.occupationIdKey}
                                                                    value={selected}
                                                                    options={options}
                                                                    getOptionLabel={(option) =>
                                                                        option.name || ''
                                                                    }
                                                                    onChange={(_, value) =>
                                                                        handleUpdate(
                                                                            i,
                                                                            market.occupationIdKey,
                                                                            value ? value.id : null
                                                                        )
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label={`*${t(
                                                                                market.occupationLabelKey,
                                                                                { lng: language }
                                                                            )}`}
                                                                            error={Boolean(
                                                                                occupationError
                                                                            )}
                                                                        />
                                                                    )}
                                                                />
                                                                <Button
                                                                    color="purple"
                                                                    border="purple"
                                                                    onClick={() =>
                                                                        setOccupationAssist({
                                                                            index: i,
                                                                            code: market.code,
                                                                        })
                                                                    }
                                                                    style={{
                                                                        width: '136px',
                                                                        height: '56px',
                                                                    }}
                                                                >
                                                                    {language === 'EN' && (
                                                                        <AIIcon
                                                                            style={{
                                                                                width: '24px',
                                                                                height: '24px',
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {t('aiAssistant', {
                                                                        lng: language,
                                                                    })}
                                                                </Button>
                                                            </Box>
                                                            {selected?.code_type ===
                                                                'Non-ANZSCO' && (
                                                                <S.ValidationMsg>{`*${t('nonAnzscoOccWarning', { lng: language })}`}</S.ValidationMsg>
                                                            )}
                                                            {selected?.name ===
                                                                'Transport truck drivers' && (
                                                                <S.ValidationMsg>{`*${t('truckDriverWarning', { lng: language })}`}</S.ValidationMsg>
                                                            )}
                                                            {occupationError ? (
                                                                <S.ValidationMsg>
                                                                    {occupationError}
                                                                </S.ValidationMsg>
                                                            ) : null}
                                                        </Box>
                                                    );
                                                })}
                                                {occupationAssist?.index === i && (
                                                    <JobTitleSelector
                                                        open
                                                        handleRecommendedJobs={() => undefined}
                                                        handleClose={closeOccupationAssist}
                                                        handleUpdate={(index, field, value) =>
                                                            handleUpdate(index, field, value)
                                                        }
                                                        index={i}
                                                        country={data[i].country}
                                                        countryCode={occupationAssist.code}
                                                        jobTitle={(
                                                            professionsByCode[
                                                                occupationAssist.code
                                                            ] ?? []
                                                        ).find(
                                                            (option) =>
                                                                option.id ===
                                                                data[i][
                                                                    marketFields(
                                                                        occupationAssist.code
                                                                    ).occupationIdKey
                                                                ]
                                                        )}
                                                        options={
                                                            professionsByCode[
                                                                occupationAssist.code
                                                            ] ?? []
                                                        }
                                                    />
                                                )}
                                            </S.GridSection>
                                            {/*{false ? (*/}
                                            {/*    <S.FlexColumn>*/}
                                            {/*        <S.FlexColumn*/}
                                            {/*            style={{*/}
                                            {/*                marginTop: '15px',*/}
                                            {/*            }}>*/}
                                            {/*            <Autocomplete*/}
                                            {/*                disablePortal*/}
                                            {/*                id="Profession Canada"*/}
                                            {/*                value={data[i].profession_can}*/}
                                            {/*                options={professions['Canada']}*/}
                                            {/*                onChange={(_, value: string | null) =>*/}
                                            {/*                    handleUpdate(*/}
                                            {/*                        i,*/}
                                            {/*                        'profession_can',*/}
                                            {/*                        value*/}
                                            {/*                    )*/}
                                            {/*                }*/}
                                            {/*                renderInput={(params) => (*/}
                                            {/*                    <TextField*/}
                                            {/*                        {...params}*/}
                                            {/*                        label={`*${t('classification', {*/}
                                            {/*                            lng: language,*/}
                                            {/*                        })}`}*/}
                                            {/*                        error={Boolean(*/}
                                            {/*                            validationMsg[i]*/}
                                            {/*                                .profession_can*/}
                                            {/*                        )}*/}
                                            {/*                    />*/}
                                            {/*                )}*/}
                                            {/*            />*/}
                                            {/*            <S.ValidationMsg>*/}
                                            {/*                {validationMsg[i].profession_can}*/}
                                            {/*            </S.ValidationMsg>*/}
                                            {/*        </S.FlexColumn>*/}
                                            {/*    </S.FlexColumn>*/}
                                            {/*) : (*/}
                                            {/*    <S.FlexColumn>*/}
                                            {/*        <Autocomplete*/}
                                            {/*            disablePortal*/}
                                            {/*            id="Profession Australia"*/}
                                            {/*            value={data[i].profession_aus}*/}
                                            {/*            options={professions['Australia']}*/}
                                            {/*            onChange={(_, value: string | null) =>*/}
                                            {/*                handleUpdate(i, 'profession_aus', value)*/}
                                            {/*            }*/}
                                            {/*            renderInput={(params) => (*/}
                                            {/*                <TextField*/}
                                            {/*                    {...params}*/}
                                            {/*                    label={`*${t('classification', {*/}
                                            {/*                        lng: language,*/}
                                            {/*                    })} 1`}*/}
                                            {/*                    error={Boolean(*/}
                                            {/*                        validationMsg[i].profession_aus*/}
                                            {/*                    )}*/}
                                            {/*                />*/}
                                            {/*            )}*/}
                                            {/*        />*/}
                                            {/*        <S.ValidationMsg>*/}
                                            {/*            {validationMsg[i].profession_aus}*/}
                                            {/*        </S.ValidationMsg>*/}
                                            {/*        <S.FlexColumn*/}
                                            {/*            style={{*/}
                                            {/*                marginTop: '15px',*/}
                                            {/*            }}>*/}
                                            {/*            <Autocomplete*/}
                                            {/*                disablePortal*/}
                                            {/*                id={`${t('profession', {*/}
                                            {/*                    lng: language,*/}
                                            {/*                })} Canada`}*/}
                                            {/*                value={data[i].profession_can}*/}
                                            {/*                options={professions['Canada']}*/}
                                            {/*                onChange={(_, value: string | null) =>*/}
                                            {/*                    handleUpdate(*/}
                                            {/*                        i,*/}
                                            {/*                        'profession_can',*/}
                                            {/*                        value*/}
                                            {/*                    )*/}
                                            {/*                }*/}
                                            {/*                renderInput={(params) => (*/}
                                            {/*                    <TextField*/}
                                            {/*                        {...params}*/}
                                            {/*                        label={`*${t('classification', {*/}
                                            {/*                            lng: language,*/}
                                            {/*                        })} 2`}*/}
                                            {/*                        error={Boolean(*/}
                                            {/*                            validationMsg[i]*/}
                                            {/*                                .profession_can*/}
                                            {/*                        )}*/}
                                            {/*                    />*/}
                                            {/*                )}*/}
                                            {/*            />*/}
                                            {/*            <S.ValidationMsg>*/}
                                            {/*                {validationMsg[i].profession_can}*/}
                                            {/*            </S.ValidationMsg>*/}
                                            {/*        </S.FlexColumn>*/}
                                            {/*    </S.FlexColumn>*/}
                                            {/*)}*/}
                                        </S.FlexColumn>
                                    </S.FlexColumn>
                                </S.EntryCard>
                            );
                        })}
                    {hasExperience && (
                        <>
                            <AddEntryFooter
                                title={t('addWorkExperience', { lng: language })}
                                addLabel={t('addWorkExperience', { lng: language })}
                                addAnotherLabel={t('addAnotherWorkExperience', { lng: language })}
                                hasEntries={data.length > 0}
                                onClick={handleAddEntry}
                            />
                        </>
                    )}
                </FormBox>
            )}
        </>
    );
}
